import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";

import {
  Table,
  TableHead,
  HeaderColumn,
  Loading,
  Modal,
  PoorHealth,
  Msg,
  JumpTo,
} from "../../../shared/components";
import { HealthCheckModel } from "../../../shared/models";
import Message from "../../../shared/services/message";
import SystemHealthItem from "./SystemHealthItem";

/**
 * Prop interface
 */
export interface SystemHealthProps {
  loading: boolean;
  isOpen: boolean;
  health: HealthCheckModel;
  canCancel: boolean | undefined;

  healthCheck: () => void;
  onClose: () => void;
}

/**
 * Page component
 */
class SystemHealth extends React.Component<SystemHealthProps> {
  constructor(props: SystemHealthProps) {
    super(props);
  }

  public shouldComponentUpdate(nextProps: SystemHealthProps) {
    return !isEqual(nextProps, this.props);
  }

  public render() {
    const { health, canCancel = true } = this.props;

    const rootStyle = classnames("code-system-health", {
      "is-passed": health.passed(),
    });

    const items = [
      {
        status: health.machine,
        titleMessageKey: "health.machine.title",
        descriptionMessageKey: "health.machine.description",
        resultText: health.osString(),
        resultDetailText: "",
      },
      {
        status: health.browser,
        titleMessageKey: "health.browser.title",
        descriptionMessageKey: "health.browser.description",
        resultText: health.browserString(),
        resultDetailText: "",
      },
      {
        status: health.cookieEnabled,
        titleMessageKey: "health.cookieEnabled.title",
        descriptionMessageKey: "health.cookieEnabled.description",
        resultText: Message.getMessageByKey(
          health.cookieEnabled ? "health.enabled" : "health.disabled",
        ),
        resultDetailText: "",
      },
      {
        status: health.webStorageEnabled,
        titleMessageKey: "health.webStorageEnabled.title",
        descriptionMessageKey: "health.webStorageEnabled.description",
        resultText: Message.getMessageByKey(
          health.webStorageEnabled ? "health.enabled" : "health.disabled",
        ),
        resultDetailText: "",
      },
      {
        status: health.apiServerConnection,
        titleMessageKey: "health.apiServerConnection.title",
        descriptionMessageKey: "health.apiServerConnection.description",
        resultText: Message.getMessageByKey(
          health.apiServerConnection
            ? "health.operational"
            : "health.notOperational",
        ),
        resultDetailText: health.apiServerConnectionError,
      },
      {
        status: health.orcaServerConnection,
        titleMessageKey: "health.orcaServerConnection.title",
        descriptionMessageKey: "health.orcaServerConnection.description",
        resultText: Message.getMessageByKey(
          health.orcaServerConnection
            ? "health.operational"
            : "health.notOperational",
        ),
        resultDetailText: health.orcaServerConnectionError,
      },
      {
        status: health.orcaServerWsConnection,
        titleMessageKey: "health.orcaServerWsConnection.title",
        descriptionMessageKey: "health.orcaServerWsConnection.description",
        resultText: Message.getMessageByKey(
          health.orcaServerWsConnection
            ? "health.operational"
            : "health.notOperational",
        ),
        resultDetailText: health.orcaServerWsConnectionError,
      },
      {
        status: health.contentsServerConnection,
        titleMessageKey: "health.contentsServerConnection.title",
        descriptionMessageKey: "health.contentsServerConnection.description",
        resultText: Message.getMessageByKey(
          health.contentsServerConnection
            ? "health.operational"
            : "health.notOperational",
        ),
        resultDetailText: health.contentsServerConnectionError,
      },
      {
        status: health.imageServerConnection,
        titleMessageKey: "health.imageServerConnection.title",
        descriptionMessageKey: "health.imageServerConnection.description",
        resultText: Message.getMessageByKey(
          health.imageServerConnection
            ? "health.operational"
            : "health.notOperational",
        ),
        resultDetailText: health.imageServerConnectionError,
      },
    ];

    return (
      <Modal
        className={rootStyle}
        isOpen={this.props.isOpen}
        title={<Msg id={"health.title"} />}
        onClickOk={() => this.props.healthCheck()}
        onClickCancel={() => this.props.onClose()}
        onClose={() => this.props.onClose()}
        okButtonLabel={<Msg id={"health.button.check"} />}
        okButtonAriaLabel="Check Again"
        hasCancelButton={canCancel}
        hasCloseButton={canCancel}
        size={"large"}
        additionalFooterContent={
          !health.passed() && (
            <Msg
              id={"health.help"}
              values={{
                a: (chunks: string) => (
                  <JumpTo to={Message.getMessageByKey("health.help.url")}>
                    {chunks}
                  </JumpTo>
                ),
              }}
            />
          )
        }
        ariaLabel="Check System"
      >
        <Loading isOpen={this.props.loading} />
        {!health.passed() && (
          <PoorHealth health={this.props.health} showWarning={true} />
        )}
        <Table bordered={true}>
          <TableHead>
            <HeaderColumn size={4}>
              <Msg id={"health.checkItem"} />
            </HeaderColumn>
            <HeaderColumn size={2}>
              <Msg id={"health.result"} />
            </HeaderColumn>
            <HeaderColumn size={1}>
              <Msg id={"health.status"} />
            </HeaderColumn>
            <HeaderColumn size={5}>
              <Msg id={"health.description"} />
            </HeaderColumn>
          </TableHead>
          {items.map((item, index) => (
            <SystemHealthItem key={index} item={item} />
          ))}
        </Table>
      </Modal>
    );
  }
}

export default SystemHealth;
